import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail, MessageSquare, Shield, Scale, CreditCard, AlertTriangle, Send, Loader2, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  { key: 'support',    label: 'General support',                    email: 'support@entreprenatives.com',  icon: MessageSquare },
  { key: 'trust',      label: 'Trust & Safety / report a vendor',   email: 'support@entreprenatives.com',  icon: Shield },
  { key: 'billing',    label: 'Billing, refunds, or payouts',       email: 'support@entreprenatives.com',  icon: CreditCard },
  { key: 'legal',      label: 'Legal & policy questions',           email: 'legal@entreprenatives.com',    icon: Scale },
  { key: 'privacy',    label: 'Privacy & data requests',            email: 'privacy@entreprenatives.com',  icon: Shield },
  { key: 'press',      label: 'Press & partnerships',               email: 'support@entreprenatives.com',  icon: AlertTriangle },
];

function Card({ children, className = '' }) {
  return (
    <div className={`bg-[#13153A]/80 backdrop-blur-md border border-purple-500/20 rounded-2xl p-6 sm:p-8 ${className}`}>
      {children}
    </div>
  );
}

export default function ContactPage() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);
  const [form, setForm] = useState({ name: '', email: '', topic: 'support', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in name, email, subject and message.');
      return;
    }
    setSending(true);
    try {
      // Reuse the public auto-seeded "homepage-contact" form slug so submissions
      // land in the same Master inbox as the homepage form.
      const r = await fetch(`${API}/forms/homepage-contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: form.name,
            email: form.email,
            subject: `[${form.topic}] ${form.subject}`,
            details: form.message,
            'how-did-you-hear-about-us': 'Contact page',
          },
        }),
      });
      if (r.ok) {
        setSent(true);
        toast.success("Message sent. We'll reply within 1 business day.");
        setForm({ name: '', email: '', topic: 'support', subject: '', message: '' });
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Could not send message — please email us directly.');
      }
    } catch {
      toast.error('Network error — please email us directly.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0D2B] via-[#13153A] to-[#0B0D2B] text-white" data-testid="contact-page">
      <Navbar />
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-28 pb-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-fuchsia-300 text-[11px] uppercase tracking-[0.18em] font-bold mb-3">Contact</p>
          <h1 className="font-syne text-3xl sm:text-5xl font-black tracking-tight mb-6" data-testid="contact-title">
            We're here to help — humans, not bots.
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg leading-relaxed max-w-3xl">
            For support, refunds, vendor questions, legal matters, or partnerships — reach the right team below
            or send us a message. We respond within 1 business day.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          {/* Direct channels */}
          <Card data-testid="contact-direct">
            <h2 className="font-syne text-xl font-bold mb-4">Direct contact</h2>
            <div className="space-y-3">
              {TOPICS.map(({ key, label, email, icon: Icon }) => (
                <a
                  key={key}
                  href={`mailto:${email}`}
                  className="flex items-center justify-between gap-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-xl px-4 py-3 transition-all"
                  data-testid={`contact-email-${key}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon size={18} className="text-fuchsia-300 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-semibold truncate">{label}</p>
                      <p className="text-zinc-400 text-xs truncate">{email}</p>
                    </div>
                  </div>
                  <Mail size={16} className="text-zinc-400 shrink-0" />
                </a>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-2 flex items-center gap-1.5"><MapPin size={11} /> Business contact</p>
              <p className="text-zinc-300 text-sm leading-relaxed">
                <strong className="text-white">EntrepreNatives</strong><br />
                Indigenous-owned marketplace & creative services platform<br />
                United States<br />
                <span className="text-zinc-400">For a postal mailing address, email <a className="underline" href="mailto:legal@entreprenatives.com">legal@entreprenatives.com</a> and we will provide it within 1 business day.</span>
              </p>
            </div>

            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-zinc-400 text-xs leading-relaxed">
                Need a refund? Read our <Link to="/refund" className="text-fuchsia-300 underline">Refund Policy</Link>, then email
                <a className="text-fuchsia-300 underline ml-1" href="mailto:support@entreprenatives.com">support@entreprenatives.com</a> with your order ID.
              </p>
            </div>
          </Card>

          {/* Form */}
          <Card data-testid="contact-form-card">
            <h2 className="font-syne text-xl font-bold mb-4">Send us a message</h2>
            {sent ? (
              <div className="bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-5 text-center" data-testid="contact-form-success">
                <p className="text-emerald-200 font-bold mb-1">Message received.</p>
                <p className="text-emerald-100/80 text-sm">We'll be in touch within 1 business day.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.name} onChange={onChange('name')} placeholder="Your name" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-500" data-testid="contact-name" required />
                  <input type="email" value={form.email} onChange={onChange('email')} placeholder="Email" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-500" data-testid="contact-email" required />
                </div>
                <select value={form.topic} onChange={onChange('topic')} className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm" data-testid="contact-topic">
                  {TOPICS.map((t) => <option key={t.key} value={t.key} className="bg-[#13153A]">{t.label}</option>)}
                </select>
                <input value={form.subject} onChange={onChange('subject')} placeholder="Subject" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-500" data-testid="contact-subject" required />
                <textarea value={form.message} onChange={onChange('message')} rows={6} placeholder="How can we help?" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder:text-zinc-500" data-testid="contact-message" required />
                <button type="submit" disabled={sending} className="w-full px-5 py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-600 hover:from-fuchsia-400 hover:to-violet-500 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
                  {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send message</>}
                </button>
                <p className="text-zinc-500 text-[11px] text-center">
                  By sending, you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
                </p>
              </form>
            )}
          </Card>
        </div>

        <p className="text-zinc-500 text-xs mt-10 leading-relaxed max-w-3xl">
          EntrepreNatives is a marketplace platform connecting customers with independent Indigenous-owned
          businesses and service providers. We are not a party to transactions between vendors and buyers, are
          not a money transmitter, and are not a financial institution. All payment processing is handled by
          Stripe, a licensed payment processor.
        </p>
      </div>
      <Footer />
    </div>
  );
}
